import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Wrench,
  DollarSign, 
  Clock, 
  Calendar,
  CheckCircle,
  AlertTriangle,
  Home,
  User,
  Phone,
  MapPin
} from 'lucide-react';

const ContractorEstimateSubmission = () => {
  const { requestId } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [requestDetails, setRequestDetails] = useState(null);
  
  const [estimate, setEstimate] = useState({
    labor_cost: '',
    materials_cost: '',
    description: '',
    estimated_hours: '',
    timeline_days: ''
  });

  useEffect(() => {
    if (requestId) {
      fetchRequestDetails();
    }
  }, [requestId]);

  const fetchRequestDetails = async () => {
    try {
      setLoading(true);
      
      // Fetch maintenance request details
      const maintenanceResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/maintenance/${requestId}`);
      if (!maintenanceResponse.ok) {
        throw new Error('Maintenance request not found');
      }
      const maintenanceData = await maintenanceResponse.json();
      
      // Fetch property details
      let propertyData = null;
      if (maintenanceData.property_id) {
        const propertyResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/properties/${maintenanceData.property_id}`);
        if (propertyResponse.ok) {
          propertyData = await propertyResponse.json();
        }
      }
      
      // Fetch tenant details  
      let tenantData = null;
      if (maintenanceData.tenant_id) {
        const tenantResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tenants/${maintenanceData.tenant_id}`);
        if (tenantResponse.ok) {
          tenantData = await tenantResponse.json();
        }
      }
      
      setRequestDetails({
        ...maintenanceData,
        property: propertyData,
        tenant: tenantData
      });
      
    } catch (err) {
      setError('Failed to load maintenance request details');
      console.error('Error fetching request details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setEstimate(prev => ({ ...prev, [field]: value }));
  };

  const calculateTotal = () => {
    const labor = parseFloat(estimate.labor_cost) || 0;
    const materials = parseFloat(estimate.materials_cost) || 0;
    return labor + materials;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      // Validate required fields
      if (!estimate.labor_cost || !estimate.materials_cost || !estimate.description || !estimate.estimated_hours || !estimate.timeline_days) {
        throw new Error('Please fill in all required fields');
      }

      const submitData = {
        labor_cost: parseFloat(estimate.labor_cost),
        materials_cost: parseFloat(estimate.materials_cost),
        description: estimate.description,
        estimated_hours: parseFloat(estimate.estimated_hours),
        timeline_days: parseInt(estimate.timeline_days)
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/maintenance/${requestId}/estimates?contractor_id=${requestDetails.contractor_id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData)
      });

      if (!response.ok) {
        throw new Error('Failed to submit estimate');
      }

      const result = await response.json();
      
      if (result.total_cost < 400) {
        setSuccess('🎉 Estimate submitted and automatically approved! (Under $400 threshold)');
      } else {
        setSuccess('✅ Estimate submitted successfully! Property owner will review and respond shortly.');
      }
      
      // Clear form
      setEstimate({
        labor_cost: '',
        materials_cost: '',
        description: '',
        estimated_hours: '',
        timeline_days: ''
      });

    } catch (err) {
      setError(err.message || 'Failed to submit estimate');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <div className="animate-spin h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading work order...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error && !requestDetails) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-red-600" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Request Not Found</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <p className="text-sm text-gray-500">Please check the link in your SMS message.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalCost = calculateTotal();
  const willAutoApprove = totalCost < 400;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Wrench className="h-8 w-8 text-blue-600 mr-2" />
            <h1 className="text-2xl font-bold text-gray-900">Legacy Property Association</h1>
          </div>
          <p className="text-gray-600">Submit Your Work Order Estimate</p>
        </div>

        {success && (
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                <span className="text-green-800">{success}</span>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Request Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Wrench className="h-5 w-5 mr-2" />
                Work Order Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-medium text-gray-900 mb-1">{requestDetails?.title}</h3>
                <p className="text-sm text-gray-600">{requestDetails?.description}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-500 uppercase tracking-wide">Category</Label>
                  <p className="text-sm font-medium">{requestDetails?.category || 'General'}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500 uppercase tracking-wide">Priority</Label>
                  <span className={`text-sm font-medium px-2 py-1 rounded ${
                    requestDetails?.priority === 'high' ? 'bg-red-100 text-red-800' :
                    requestDetails?.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {requestDetails?.priority || 'Medium'}
                  </span>
                </div>
              </div>

              {/* Property Information */}
              <div className="pt-4 border-t">
                <div className="flex items-center mb-2">
                  <Home className="h-4 w-4 mr-2 text-gray-500" />
                  <Label className="text-sm font-medium">Property Information</Label>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-gray-900">
                    {requestDetails?.property?.name || 'Property Name Not Available'}
                  </p>
                  <div className="flex items-center text-sm text-gray-600">
                    <MapPin className="h-3 w-3 mr-1" />
                    {requestDetails?.property?.address || 'Address Not Available'}
                  </div>
                </div>
              </div>

              {/* Tenant Contact */}
              <div className="pt-4 border-t">
                <div className="flex items-center mb-2">
                  <User className="h-4 w-4 mr-2 text-gray-500" />
                  <Label className="text-sm font-medium">Tenant Contact</Label>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-gray-900">
                    {requestDetails?.tenant?.name || 'Tenant Name Not Available'}
                  </p>
                  {requestDetails?.tenant?.phone && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Phone className="h-3 w-3 mr-1" />
                      {requestDetails.tenant.phone}
                    </div>
                  )}
                </div>
              </div>

              {/* Request Images */}
              {requestDetails?.images && requestDetails.images.length > 0 && (
                <div className="pt-4 border-t">
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">Request Images</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {requestDetails.images.map((image, index) => (
                      <img
                        key={index}
                        src={`${process.env.REACT_APP_BACKEND_URL}/uploads/${image}`}
                        alt={`Request ${index + 1}`}
                        className="w-full h-24 object-cover rounded border"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Estimate Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <DollarSign className="h-5 w-5 mr-2" />
                Submit Your Estimate
              </CardTitle>
            </CardHeader>
            <CardContent>
              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
                    <span className="text-red-800">{error}</span>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Cost Breakdown */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="labor_cost">Labor Cost *</Label>
                    <Input
                      id="labor_cost"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={estimate.labor_cost}
                      onChange={(e) => handleChange('labor_cost', e.target.value)}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="materials_cost">Materials Cost *</Label>
                    <Input
                      id="materials_cost"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={estimate.materials_cost}
                      onChange={(e) => handleChange('materials_cost', e.target.value)}
                      required
                    />
                  </div>
                </div>

                {/* Total Cost Display */}
                <div className="p-4 bg-gray-50 rounded-lg border">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-700">Total Estimate:</span>
                    <span className="text-2xl font-bold text-gray-900">
                      ${totalCost.toFixed(2)}
                    </span>
                  </div>
                  {willAutoApprove && totalCost > 0 && (
                    <p className="text-sm text-green-600 mt-2">
                      ✓ This estimate will be automatically approved (under $400 threshold)
                    </p>
                  )}
                  {!willAutoApprove && totalCost >= 400 && (
                    <p className="text-sm text-orange-600 mt-2">
                      ⚠️ This estimate requires property owner approval (over $400 threshold)
                    </p>
                  )}
                </div>

                {/* Timeline Information */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="estimated_hours" className="flex items-center">
                      <Clock className="h-4 w-4 mr-2" />
                      Estimated Hours *
                    </Label>
                    <Input
                      id="estimated_hours"
                      type="number"
                      step="0.5"
                      min="0"
                      placeholder="0"
                      value={estimate.estimated_hours}
                      onChange={(e) => handleChange('estimated_hours', e.target.value)}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="timeline_days" className="flex items-center">
                      <Calendar className="h-4 w-4 mr-2" />
                      Days to Complete *
                    </Label>
                    <Input
                      id="timeline_days"
                      type="number"
                      min="1"
                      placeholder="1"
                      value={estimate.timeline_days}
                      onChange={(e) => handleChange('timeline_days', e.target.value)}
                      required
                    />
                  </div>
                </div>

                {/* Work Description */}
                <div>
                  <Label htmlFor="description">Work Description *</Label>
                  <Textarea
                    id="description"
                    placeholder="Describe the work to be performed, materials needed, and any special considerations..."
                    rows={4}
                    value={estimate.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    required
                  />
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <Button 
                    type="submit" 
                    disabled={submitting || totalCost <= 0}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    size="lg"
                  >
                    {submitting ? 'Submitting Estimate...' : 'Submit Estimate'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ContractorEstimateSubmission;